import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { CREATE_ASSET_MUTATION } from '@/apollo/schemas/assets/createAsset';
import { AdminActionWrapper } from '@/components/common/AdminActionWrapper';
import { useAuth } from '@/context/CoginitoAuthProvider';
import { useToast } from '@/hooks/use-toast';
import { ISSUABLE_CHAINS } from '@/utils/chains';
import { SELECTABLE_COMPLIANCE_MODES } from '@/utils/compliance';
import { STANDARD_TOKEN_DECIMALS } from '@/utils/tokenSupply';

import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';

const formSchema = z.object({
  name: z.string().min(1, 'Asset name is required'),
  type: z.string().min(1, 'Asset type is required'),
  gold_weight: z.coerce
    .number()
    .positive('Gold weight must be a positive number'),
  copper_weight: z.coerce
    .number()
    .positive('Copper weight must be a positive number'),
  copper_price: z.coerce.number().min(0).optional().or(z.literal(0)),
  certificateUrl: z.string().url('Must be a valid URL'),
  erc3643Name: z.string().min(1, 'ERC-3643 token name is required'),
  chain: z.string().min(1, 'Chain is required'),
  complianceMode: z.string().min(1, 'Compliance mode is required'),
  erc3643Symbol: z.string().min(1, 'ERC-3643 token symbol is required'),
  // 18 is the platform standard and the default. Fewer is allowed — a 6- or 8-decimal token
  // is a legitimate thing to issue — but more is not: nothing here needs it, and a value
  // above 18 is far likelier to be a typo than an intention.
  erc3643Decimals: z.coerce
    .number()
    .int('Decimals must be a whole number')
    .min(0, 'Decimals cannot be negative')
    .max(
      STANDARD_TOKEN_DECIMALS,
      `Decimals cannot exceed ${STANDARD_TOKEN_DECIMALS}`,
    ),
  erc3643TotalSupply: z.string().min(1, 'Total supply is required'),
  imageBase64: z.string().optional(),
  availableTokens: z.coerce.number().min(0).optional().or(z.literal(0)),
  pricePerToken: z.coerce.number().min(0).optional().or(z.literal(0)),
  tokenizedPercentage: z.coerce.number().min(0).optional().or(z.literal(0)),
  stageMultiplier: z.coerce.number().min(0).optional().or(z.literal(0)),
  totalTokens: z.coerce.number().min(0).optional().or(z.literal(0)),
  minimumInvestmentAmount: z.coerce.number().min(0).optional().or(z.literal(0)),
  isActive: z.boolean().default(true),
  requireAccreditedInvestor: z.boolean().default(false),
});

/**
 * Total supply is entered in TOKENS, not in the chain's smallest unit.
 *
 * The backend multiplies by 10^decimals on the way to the chain, so a supply that has
 * already been scaled gets scaled twice. That is not hypothetical: an asset was created with
 * a supply of 1000000000000000000000 — 1000 tokens times 10^18 — and went on-chain with
 * 10^39. The request succeeded at every step and the mistake only surfaced much later.
 *
 * The threshold is 10^decimals because that is the real boundary: for an 18-decimal token
 * one whole token is 10^18, so any human supply at or above that has already been scaled.
 * The largest asset on the platform is 1e14 tokens, four orders of magnitude clear of it.
 */
const validatedFormSchema = formSchema.superRefine((values, ctx) => {
  const decimals = values.erc3643Decimals;
  const supply = Number(String(values.erc3643TotalSupply).trim());

  if (!Number.isFinite(supply) || supply <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['erc3643TotalSupply'],
      message: 'Total supply must be a number greater than zero.',
    });
    return;
  }

  // Below 6 decimals the threshold gets small enough that a genuine supply could exceed it,
  // so the check would start refusing valid input.
  if (decimals >= 6 && supply >= 10 ** decimals) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['erc3643TotalSupply'],
      message: `This looks like a wei-scaled amount. Enter the number of tokens — the platform multiplies by 10^${decimals} at deploy. Did you mean ${supply / 10 ** decimals}?`,
    });
  }
});

interface AddAssetDialogProps {
  refetchData: () => void;
}

const AddAssetDialog = ({ refetchData }: AddAssetDialogProps) => {
  const [open, setOpen] = useState(false);
  // imagePreview and handleImageUpload below drive the asset-image FormField
  // that is currently commented out in the form; keep both until it is restored.
  // eslint-disable-next-line
  const [imagePreview, setImagePreview] = useState<string>('');
  const { toast } = useToast();
  const { session } = useAuth();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(validatedFormSchema),
    defaultValues: {
      name: '',
      type: '',
      gold_weight: 0,
      copper_weight: 0,
      copper_price: undefined,
      certificateUrl: '',
      erc3643Name: '',
      chain: 'AVALANCHE_FUJI',
      complianceMode: 'ACE_ONLY',
      erc3643Symbol: '',
      erc3643Decimals: STANDARD_TOKEN_DECIMALS,
      erc3643TotalSupply: '',
      imageBase64: undefined,
      availableTokens: undefined,
      pricePerToken: undefined,
      tokenizedPercentage: undefined,
      stageMultiplier: undefined,
      totalTokens: undefined,
      minimumInvestmentAmount: undefined,
      isActive: true,
      requireAccreditedInvestor: false,
    },
  });

  // Accredited-only is enforced by an ACE policy, so it means nothing under the registry
  // gate. The backend rejects that combination; clearing it here keeps the form from
  // submitting something it will refuse.
  const complianceMode = form.watch('complianceMode');
  const accreditedAvailable = complianceMode !== 'LEGACY';

  const [createAsset, { loading }] = useMutation(CREATE_ASSET_MUTATION, {
    onCompleted: () => {
      toast({
        variant: 'success',
        title: 'Asset Created',
        description: 'Asset has been created successfully.',
      });
      setOpen(false);
      form.reset();
      setImagePreview('');
      refetchData();
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Creation Failed',
        description: error.message,
      });
    },
  });

  // eslint-disable-next-line
  const handleImageUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        variant: 'destructive',
        title: 'Invalid File Type',
        description: 'Please upload an image file.',
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setImagePreview(base64String);
      onChange(base64String);
    };
    reader.onerror = () => {
      toast({
        variant: 'destructive',
        title: 'Upload Failed',
        description: 'Failed to read the image file.',
      });
    };
    reader.readAsDataURL(file);
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!session) {
      toast({
        variant: 'destructive',
        title: 'Authentication Required',
        description: 'Please sign in to create an asset.',
      });
      return;
    }

    await createAsset({
      variables: {
        name: values.name,
        type: values.type,
        gold_weight: values.gold_weight,
        copper_weight: values.copper_weight,
        certificateUrl: values.certificateUrl,
        erc3643Name: values.erc3643Name,
        chain: values.chain,
        complianceMode: values.complianceMode,
        erc3643Symbol: values.erc3643Symbol,
        erc3643Decimals: values.erc3643Decimals,
        erc3643TotalSupply: values.erc3643TotalSupply,
        ...(values.copper_price && { copper_price: values.copper_price }),
        ...(values.imageBase64 && { imageBase64: values.imageBase64 }),
        ...(values.availableTokens && {
          availableTokens: values.availableTokens,
        }),
        ...(values.pricePerToken && {
          pricePerToken: values.pricePerToken,
        }),
        ...(values.tokenizedPercentage && {
          tokenizedPercentage: values.tokenizedPercentage,
        }),
        ...(values.stageMultiplier && {
          stageMultiplier: values.stageMultiplier,
        }),
        ...(values.totalTokens && { totalTokens: values.totalTokens }),
        ...(values.minimumInvestmentAmount && {
          minimumInvestmentAmount: values.minimumInvestmentAmount,
        }),
        isActive: values.isActive,
        requireAccreditedInvestor:
          accreditedAvailable && values.requireAccreditedInvestor,
      },
      context: {
        headers: {
          identity: session?.getIdToken().getJwtToken(),
        },
      },
    });
  };

  const handleDialogChange = (open: boolean) => {
    setOpen(open);
    if (!open) {
      form.reset();
      setImagePreview('');
    }
  };

  return (
    <>
      <AdminActionWrapper>
        <Button onClick={() => setOpen(true)}>
          <Plus size={16} className="mr-2" />
          Add Asset
        </Button>
      </AdminActionWrapper>

      <Dialog open={open} onOpenChange={handleDialogChange}>
        <DialogContent className="bg-white max-h-[90vh] max-w-2xl flex flex-col">
          <DialogHeader>
            <DialogTitle>Add New Asset</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4 overflow-y-auto pr-2"
            >
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Asset Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter asset name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Gold, Silver" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gold_weight"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gold Weight (oz)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Enter gold weight"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="copper_weight"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Copper Weight (lbs)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Enter copper weight"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="copper_price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Copper Price</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Enter copper price"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="pricePerToken"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price Per Token</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Enter price per token"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="totalTokens"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Total Tokens</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Enter total tokens"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="availableTokens"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Available Tokens</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Enter available tokens"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tokenizedPercentage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tokenized Percentage</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Enter tokenized percentage"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="stageMultiplier"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Stage Multiplier</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Enter stage multiplier"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="minimumInvestmentAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Minimum Investment Amount</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Enter minimum investment"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="erc3643Name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ERC-3643 Token Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter token name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="chain"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Chain</FormLabel>
                      <FormControl>
                        {/* Which network the token is deployed on. Fixed list rather than a
                            lookup: these are the chains the backend has contracts and a CRE
                            mint workflow configured for, and creating on any other is
                            rejected server-side with a message naming what is missing. */}
                        <select
                          {...field}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {ISSUABLE_CHAINS.map((chain) => (
                            <option key={chain.id} value={chain.id}>
                              {chain.label}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="complianceMode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Compliance</FormLabel>
                      <FormControl>
                        {/* Which identity gate this asset is issued under. Fixed at
                            creation: moving an asset between gates means migrating every
                            holder, not flipping a setting.

                            Not filtered by the selected chain on purpose. Whether a chain
                            can provide a mode depends on backend configuration the panel
                            does not see (a PolicyEngine for ACE, a claim issuer and KYC
                            workflow for the registry), and duplicating those rules here
                            would drift from them. An unsupported combination is rejected
                            server-side with a message naming the missing setting. */}
                        <select
                          {...field}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {SELECTABLE_COMPLIANCE_MODES.map((mode) => (
                            <option key={mode.id} value={mode.id}>
                              {mode.label}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        {
                          SELECTABLE_COMPLIANCE_MODES.find(
                            (mode) => mode.id === field.value,
                          )?.description
                        }
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="requireAccreditedInvestor"
                  render={({ field }) => (
                    <FormItem className="col-span-2 flex items-center justify-between rounded-lg border p-3">
                      <div>
                        <FormLabel>Accredited investors only</FormLabel>
                        {/* Fixed at creation. Wallets without an accredited credential
                            cannot receive or move the token, so leave it off unless the
                            offering is accredited-only. Whether the chain has the
                            accredited policy is decided server-side; an unsupported chain
                            is rejected with a message saying so. */}
                        <p className="text-sm text-muted-foreground">
                          {accreditedAvailable
                            ? 'Adds the ACE accredited investor policy ahead of KYC. Only wallets with an accredited credential can hold or transfer this token. Can be turned on later from Edit, but not off.'
                            : 'Needs an ACE compliance mode (ACE or Both).'}
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={accreditedAvailable && field.value}
                          onCheckedChange={field.onChange}
                          disabled={!accreditedAvailable}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="erc3643Symbol"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ERC-3643 Token Symbol</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter token symbol (e.g., DOME)"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="erc3643Decimals"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ERC-3643 Decimals</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          max={STANDARD_TOKEN_DECIMALS}
                          placeholder="18"
                          {...field}
                        />
                      </FormControl>
                      <p className="text-sm text-muted-foreground">
                        {STANDARD_TOKEN_DECIMALS} unless there is a reason to
                        use fewer. Never more.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="erc3643TotalSupply"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ERC-3643 Total Supply</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. 1000000" {...field} />
                      </FormControl>
                      <p className="text-sm text-muted-foreground">
                        In tokens, not wei. The platform multiplies by 10^
                        {form.watch('erc3643Decimals') ||
                          STANDARD_TOKEN_DECIMALS}{' '}
                        at deploy.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="certificateUrl"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <FormLabel>Certificate URL</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://example.com/certificate.pdf"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="col-span-2 flex items-center justify-between rounded-lg border p-3">
                      <div>
                        <FormLabel>Active Status</FormLabel>
                        <p className="text-sm text-muted-foreground">
                          Set whether this asset is active or inactive
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* <FormField
                  control={form.control}
                  name="imageBase64"
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormItem className="col-span-2">
                      <FormLabel>Asset Image</FormLabel>
                      <FormControl>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e, onChange)}
                              name={field.name}
                              onBlur={field.onBlur}
                              ref={field.ref}
                              className="cursor-pointer"
                            />
                            <Upload className="h-4 w-4 text-gray-500" />
                          </div>
                          {imagePreview && (
                            <div className="mt-2 border rounded-md p-2">
                              <img
                                src={imagePreview}
                                alt="Preview"
                                className="max-h-40 object-contain mx-auto"
                              />
                            </div>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                /> */}
              </div>

              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Asset'
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddAssetDialog;
